import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeUp, viewport } from "@/lib/motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "Working with Hadil was a truly invigorating experience. She is deeply multifaceted and was able to hold a level of reflection that I hadn't been able to access before. Hadil brings a powerful balance of depth, conscious exploration, and practicality, which allowed me to unlock new understanding in both my personal evolution and the growth of my business.",
    name: "Rhiannon Rosalind",
    pronouns: "(she/her)",
    role: "Creator + CEO, Conscious Economics + Lunar Studios",
  },
  {
    quote:
      "Working with Hadil helped me access layers of myself I had been avoiding or simply didn't know how to articulate. I came in thinking I needed strategy and direction, but what emerged was much deeper, clarity around who I am, what I value, and the vision I'm actually here to contribute. The experience didn't just give me answers; it shifted the way I see myself and my role within something larger.",
    name: "Sonia M",
    role: "1:1 Journey participant",
  },
  {
    quote:
      "Working with Hadil was a deeply grounding and expansive experience. She helped me connect to my deeper \u201Cwhy\u201D and navigate the different identities I hold, especially as a mother and a woman building a business. What she created wasn't just a brand; it was a reflection of my essence. I now feel seen in my brand, not just represented.",
    name: "Aseel El-Baba",
    role: "Financial Therapist, Holistic Optimal Wealth",
  },
  {
    quote:
      "I'm still in awe of our unique and deeply meaningful meetings. Hadil took me into such profound layers of my long spiritual journey, and I truly appreciated all our reflections and insights. It opened up so many layers of contemplation that stayed with me throughout my days.",
    name: "Zahra, PhD",
    role:
      "PNAAEE CEE-Change Fellow 2025, Visiting Instructor with Cornell University's Civic Ecology Lab",
  },
  {
    quote:
      "It was a transformative, enlightening, rewarding, fulfilling, colourful weekend of learning, witnessing, sharing, supporting, laughing, dreaming, dancing and breaking bread with a group of outstanding and powerful women. I will carry their warmth, power and wisdom with me when my path is smooth and when my path is bumpy. I will keep dancing through.",
    name: "Participant",
    role: "INSPIRE retreat",
  },
  {
    quote:
      "There are very few people who can hold both depth and structure the way Hadil does. I felt deeply seen, but also challenged to look beyond surface goals and reconnect with the essence behind my vision. I walked away with more clarity, courage, and a renewed sense of direction.",
    name: "Alex",
    role: "1:1 Journey participant",
  },
];

export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = TESTIMONIALS.length;

  const go = useCallback((delta) => {
    setIndex((i) => (i + delta + total) % total);
  }, [total]);

  // auto-advance every 8s, paused on hover
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 8000);
    return () => clearInterval(t);
  }, [paused, go]);

  const current = TESTIMONIALS[index];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-sand py-24 md:py-32 overflow-hidden"
    >
      {/* faint orbital */}
      <svg
        className="absolute -bottom-32 -right-20 w-[460px] opacity-15 pointer-events-none"
        viewBox="0 0 400 400"
        fill="none"
      >
        <circle cx="200" cy="200" r="190" stroke="#5C5549" strokeWidth="0.5" strokeDasharray="2 6" />
        <circle cx="200" cy="200" r="130" stroke="#5C5549" strokeWidth="0.5" strokeDasharray="1 3" />
        <circle cx="200" cy="200" r="70" stroke="#5C5549" strokeWidth="0.5" />
      </svg>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUp}
              className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-3"
            >
              ✦ &nbsp; 06 , Testimonials
            </motion.p>
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUp}
              data-testid="testimonials-title"
              className="font-serif text-4xl md:text-5xl text-charcoal leading-[1] tracking-tight"
            >
              Reflections{" "}
              <span className="italic text-bronze">from the journey.</span>
            </motion.h2>
          </div>
          <p className="hidden md:block font-serif italic text-bronze text-sm">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
        </div>

        {/* carousel */}
        <div
          className="relative bg-bone border border-stone/30 px-8 md:px-16 py-10 md:py-14 min-h-[320px] flex flex-col justify-between"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <span className="font-serif italic text-7xl text-bronze leading-none absolute top-6 left-8 select-none">
            &ldquo;
          </span>

          <AnimatePresence mode="wait">
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="pt-10"
              data-testid={`testimonial-${index}`}
            >
              <blockquote className="text-ink leading-relaxed font-light text-base md:text-lg max-w-3xl">
                {current.quote}
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-stone/30 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <p className="font-serif italic text-xl text-charcoal">
                  {current.name}
                  {current.pronouns && (
                    <span className="text-stone text-sm not-italic font-sans ml-2">
                      {current.pronouns}
                    </span>
                  )}
                </p>
                <span className="text-bronze">·</span>
                <p className="text-[10px] uppercase tracking-[0.22em] text-stone">
                  {current.role}
                </p>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* controls */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-2" data-testid="testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Show testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-[3px] transition-all duration-500 ${
                  i === index ? "w-10 bg-bronze" : "w-5 bg-stone/40 hover:bg-stone/70"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="Previous"
              data-testid="testimonial-prev"
              className="w-10 h-10 rounded-full border border-stone/40 flex items-center justify-center text-ink hover:bg-charcoal hover:text-bone hover:border-charcoal transition-all duration-500"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next"
              data-testid="testimonial-next"
              className="w-10 h-10 rounded-full border border-stone/40 flex items-center justify-center text-ink hover:bg-charcoal hover:text-bone hover:border-charcoal transition-all duration-500"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
